import React, { useState } from "react";
import PropTypes from "prop-types";
import {
    useFloating,
    useHover,
    useFocus,
    useInteractions,
    FloatingPortal,
    offset,
    flip,
    shift,
} from "@floating-ui/react";
import styled from "styled-components";
import { TOOLTIP_SURFACE } from "../StyledComponents/tooltipSurface";

const TOOLTIP_STYLE = {
    ...TOOLTIP_SURFACE,
    padding: "4px 10px",
    zIndex: 9999,
    pointerEvents: "none",
};

// Attaches a hint to an element that already exists — unlike PathwayTooltip, which
// renders its own info-icon trigger. Use it instead of a native title attribute:
// the title styling cannot be themed and its ~1s delay reads as unresponsive.
const PathwayHint = ({ children, hint, placement = "top", stretch = false }) => {
    const [isOpen, setIsOpen] = useState(false);

    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        placement,
        middleware: [offset(6), flip(), shift({ padding: 8 })],
    });

    const hover = useHover(context, { delay: { open: 120, close: 0 } });
    const focus = useFocus(context);
    const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus]);

    if (!hint) {
        return children;
    }

    return (
        <>
            {/* The hint hangs off a wrapper rather than the child: a disabled
                button fires no pointer events, so its own hint would never show. */}
            <ReferenceSpan ref={refs.setReference} $stretch={stretch} {...getReferenceProps()}>
                {children}
            </ReferenceSpan>
            {isOpen && (
                <FloatingPortal>
                    <div
                        ref={refs.setFloating}
                        style={{ ...TOOLTIP_STYLE, ...floatingStyles }}
                        {...getFloatingProps()}
                    >
                        {hint}
                    </div>
                </FloatingPortal>
            )}
        </>
    );
};

PathwayHint.propTypes = {
    hint: PropTypes.node,
    placement: PropTypes.string,
    stretch: PropTypes.bool,
};

// A block wrapper leaves an inline-flex child at its content width, which silently
// narrows a control that is supposed to match the buttons stacked beside it. `stretch`
// makes the wrapper hand its full width to that single child instead.
const ReferenceSpan = styled.span`
    display: ${(props) => (props.$stretch ? "flex" : "block")};

    ${(props) =>
        props.$stretch &&
        `
        > * {
            flex: 1;
            min-width: 0;
        }
    `}
`;

export default PathwayHint;
